import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import EmbedViewer from '../components/EmbedViewer';
import { I18nProvider } from '../i18n';
import { customerApi, Collectiv3Scene } from '../services/customerApi';
import { SceneConfig } from '../types/sceneConfig';

const EMBED_CAMERA: Record<string, {
  pos?: [number, number, number];
  lookAt?: [number, number, number];
}> = {
  medical_lake: {
    pos: [-2285, 900, 3777],
    lookAt: [-2285, 736, 3777],
  },
  medical_lake_neighbourhood: {
    pos: [-2311.245, 780.077, 3777.299],
    lookAt: [-2287.213, 743.008, 3773.227],
  },
  medical_lake_house: {
    pos: [-2285, 900, 3777],
    lookAt: [-2285, 736, 3777],
  },
};

type RawScene = Partial<SceneConfig> & { _id?: string };

function validCameraTuple(value: unknown): [number, number, number] | undefined {
  if (!Array.isArray(value) || value.length !== 3 || !value.every(Number.isFinite)) return undefined;
  return value as [number, number, number];
}

function toEmbedSceneConfig(apiScene: RawScene): SceneConfig {
  const sceneId = apiScene.sceneId ?? apiScene.sceneName ?? apiScene._id;
  if (!sceneId) throw new Error('Scene is missing a sceneId or sceneName');

  const sceneKey = sceneId.toLowerCase();
  const camera = EMBED_CAMERA[sceneKey] ?? {};
  const useFixedMedicalLakeCamera = sceneKey === 'medical_lake_neighbourhood';

  return {
    sceneId,
    sceneName: apiScene.sceneName ?? sceneId,
    gaus: apiScene.gaus ?? undefined,
    manifest: apiScene.manifest ?? undefined,
    audio: apiScene.audio ?? undefined,
    envMap: apiScene.envMap ?? undefined,
    imagePoses: apiScene.imagePoses ?? undefined,
    thumbnail: apiScene.thumbnail ?? undefined,
    defaultCameraPos: useFixedMedicalLakeCamera
      ? camera.pos
      : validCameraTuple(apiScene.defaultCameraPos) ?? camera.pos,
    defaultLookAt: useFixedMedicalLakeCamera
      ? camera.lookAt
      : validCameraTuple(apiScene.defaultLookAt) ?? camera.lookAt,
    sceneType: apiScene.sceneType ?? undefined,
    coordinateFrame: apiScene.coordinateFrame ?? undefined,
    hasGeoAlignment: apiScene.hasGeoAlignment ?? false,
    supportsRecording: apiScene.supportsRecording ?? false,
  };
}

/** Route: /viewer-embed?scene=<sceneId> */
export default function EmbedViewerPage() {
  const [searchParams] = useSearchParams();
  const sceneId = searchParams.get('scene') ?? 'medical_lake_neighbourhood';
  const [scene, setScene] = useState<SceneConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    setScene(null);
    setError(null);
    customerApi.searchCollectiv3Scenes(sceneId)
      .then((response) => {
        const key = sceneId.toLowerCase();
        const match = response.success
          ? (response.data as Collectiv3Scene[] | undefined)?.find(
              (candidate) => candidate.sceneName.toLowerCase() === key
                || candidate.sceneId?.toLowerCase() === key,
            )
          : undefined;
        if (!match) throw new Error(`Scene "${sceneId}" not found`);
        if (!cancelled) setScene(toEmbedSceneConfig(match as RawScene));
      })
      .catch((loadError) => {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : 'Failed to load scene');
        }
      });

    return () => { cancelled = true; };
  }, [sceneId]);

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-950">
        <p className="text-red-400 text-sm">{error}</p>
      </div>
    );
  }

  if (!scene) {
    return <div className="h-screen w-screen bg-gray-950" />;
  }

  return (
    <I18nProvider>
      <div style={{ width: '100vw', height: '100vh' }}>
        <EmbedViewer scene={scene} />
      </div>
    </I18nProvider>
  );
}
